import { Link } from 'react-router-dom'
import type { StorefrontReturnTarget } from '@/features/clubs/storefrontReturn'
import { StorefrontClubSwitcher } from './StorefrontClubSwitcher'
import styles from './StorefrontReturn.module.css'

/**
 * THE CLUB THIS TAB WALKED OUT OF, STANDING WHERE OUR WORDMARK STANDS.
 *
 * THE OWNER'S FINDING. Tapping "All players" on a club's storefront took him to the
 * platform-wide list with nothing left of the club he had been reading. `205a7913` fixed
 * the DESTINATION's honesty by renaming the menu rows "All …"; this is the ORIGIN's half.
 *
 * AND THE OWNER'S CORRECTION, 2026-09-07. The first shape of this component kept our
 * wordmark first and put the club in a pill reading "← Back to «club»", on the argument
 * that a bare club mark in a header reads as WHOSE PAGE THIS IS. He saw it on production
 * and refused both halves: while a club is remembered the header carries the club's mark
 * and its name and nothing else of ours, and the words "Back to" are gone with the
 * wordmark. The mark and the name ARE the link back — that is the mockup he approved,
 * and it is not re-argued here.
 *
 * THE SCREEN READER KEEPS THE SENTENCE. The visible words that named the destination are
 * gone, so the link carries it as its accessible name instead: a name on the link, never
 * copy on the page. A club's logo is decorative beside its own name, so the mark is
 * hidden and the name is left readable — if this label were ever dropped the link would
 * still announce the club rather than announcing nothing.
 *
 * A GUEST GETS THIS TOO, and he is the easiest to lose. The memory is about ARRIVAL, so
 * someone browsing a club he has never joined gets his way back exactly as a member
 * does. Nothing here asks about membership; the only thing that does is the chevron,
 * which is his own list of clubs and correctly renders nothing when he has none.
 */
export function StorefrontReturn({
  target,
  switcher = true,
}: {
  target: StorefrontReturnTarget
  /** `false` for a staff member's own club: the switcher is for storefront visitors. */
  switcher?: boolean
}) {
  return (
    <span className={styles.group}>
      <Link className={styles.back} to={target.href} aria-label={`Back to ${target.name}`}>
        <ClubMark target={target} />
        <span className={styles.name}>{target.name}</span>
      </Link>
      {/* The way into ANOTHER club — the same control the club's own bar carries, told
          which bar it is standing on. On a platform page the named club may be one the
          viewer never joined, so the gate differs by exactly one line and that line
          lives in `features/clubs/storefrontClubSwitch.ts`, not here. */}
      {switcher ? <StorefrontClubSwitcher club={{ slug: target.slug, name: target.name }} host="platform" /> : null}
    </span>
  )
}

/** The club's logo, or its initials when it has none — so the mark is always there and
 *  the bar never changes shape between one club and the next. */
function ClubMark({ target }: { target: StorefrontReturnTarget }) {
  return (
    <span className={styles.mark} aria-hidden="true">
      {target.logoUrl ? (
        <img className={styles.markImage} src={target.logoUrl} alt="" width={24} height={24} />
      ) : (
        initials(target.name)
      )}
    </span>
  )
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')
}
